import React, { useState } from "react";
import {
  Columns,
  Smartphone,
  Tablet,
  Laptop,
  Monitor,
  X,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import type { ResponsiveColumnWidths, ResponsivePadding, PaddingValues, BreakpointKey } from "../types";
import { BREAKPOINTS } from "../types";

interface ColumnStyleModalProps {
  isOpen: boolean;
  columnIndex: number;
  columnWidths: ResponsiveColumnWidths;
  widthType: "percent" | "pixel" | "auto";
  textAlign: "left" | "center" | "right";
  verticalAlign: "top" | "middle" | "bottom";
  padding: ResponsivePadding;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  onColumnWidthsChange: (widths: ResponsiveColumnWidths) => void;
  onWidthTypeChange: (type: "percent" | "pixel" | "auto") => void;
  onTextAlignChange: (align: "left" | "center" | "right") => void;
  onVerticalAlignChange: (align: "top" | "middle" | "bottom") => void;
  onPaddingChange: (padding: ResponsivePadding) => void;
  onBackgroundColorChange: (color: string) => void;
  onTextColorChange: (color: string) => void;
  onBorderColorChange: (color: string) => void;
  onApply: () => void;
  onClose: () => void;
}

const percentOptions = [
  { value: "", label: "Auto" },
  { value: "5", label: "5%" },
  { value: "10", label: "10%" },
  { value: "15", label: "15%" },
  { value: "20", label: "20%" },
  { value: "25", label: "25%" },
  { value: "30", label: "30%" },
  { value: "33", label: "33%" },
  { value: "40", label: "40%" },
  { value: "50", label: "50%" },
];

const paddingOptions = [
  { value: "0", label: "0" },
  { value: "2", label: "2px" },
  { value: "4", label: "4px" },
  { value: "6", label: "6px" },
  { value: "8", label: "8px" },
  { value: "10", label: "10px" },
  { value: "12", label: "12px" },
  { value: "16", label: "16px" },
  { value: "20", label: "20px" },
  { value: "24", label: "24px" },
];

const defaultPadding: PaddingValues = { top: "8", right: "12", bottom: "8", left: "12" };

const breakpointIcons: Record<BreakpointKey, React.FC<{ className?: string }>> = {
  xs: Smartphone,
  sm: Tablet,
  md: Laptop,
  lg: Monitor,
  xl: Monitor,
};

export const ColumnStyleModal: React.FC<ColumnStyleModalProps> = ({
  isOpen,
  columnIndex,
  columnWidths,
  widthType,
  textAlign,
  verticalAlign,
  padding,
  backgroundColor,
  textColor,
  borderColor,
  onColumnWidthsChange,
  onWidthTypeChange,
  onTextAlignChange,
  onVerticalAlignChange,
  onPaddingChange,
  onBackgroundColorChange,
  onTextColorChange,
  onBorderColorChange,
  onApply,
  onClose,
}) => {
  const [expandedBreakpoint, setExpandedBreakpoint] = useState<BreakpointKey | null>("xs");

  if (!isOpen) return null;

  const updatePaddingForBreakpoint = (
    breakpoint: BreakpointKey,
    field: keyof PaddingValues,
    value: string
  ) => {
    onPaddingChange({
      ...padding,
      [breakpoint]: {
        ...padding[breakpoint],
        [field]: value,
      },
    });
  };

  const copyPaddingToAll = (sourceBreakpoint: BreakpointKey) => {
    const sourcePadding = padding[sourceBreakpoint];
    const newPadding: ResponsivePadding = {
      xs: { ...sourcePadding },
      sm: { ...sourcePadding },
      md: { ...sourcePadding },
      lg: { ...sourcePadding },
      xl: { ...sourcePadding },
    };
    onPaddingChange(newPadding);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Columns className="w-5 h-5" />
            Column {columnIndex + 1} Style Settings
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Configure column width, padding, and styles for different screen sizes.
          Mobile breakpoint (xs) covers 340px-1079px.
        </p>

        <div className="flex flex-col gap-6">
          {/* Width Type Toggle */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Width Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onWidthTypeChange("auto")}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  widthType === "auto"
                    ? "bg-primary-main text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Auto
              </button>
              <button
                type="button"
                onClick={() => onWidthTypeChange("percent")}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  widthType === "percent"
                    ? "bg-primary-main text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Percentage (%)
              </button>
              <button
                type="button"
                onClick={() => onWidthTypeChange("pixel")}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  widthType === "pixel"
                    ? "bg-primary-main text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Fixed (px)
              </button>
            </div>
          </div>

          {/* Text Alignment */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Text Alignment</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onTextAlignChange("left")}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  textAlign === "left"
                    ? "bg-primary-main text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <AlignLeft className="w-4 h-4" />
                Left
              </button>
              <button
                type="button"
                onClick={() => onTextAlignChange("center")}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  textAlign === "center"
                    ? "bg-primary-main text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <AlignCenter className="w-4 h-4" />
                Center
              </button>
              <button
                type="button"
                onClick={() => onTextAlignChange("right")}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  textAlign === "right"
                    ? "bg-primary-main text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <AlignRight className="w-4 h-4" />
                Right
              </button>
            </div>
          </div>

          {/* Vertical Alignment */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Vertical Alignment</label>
            <div className="flex gap-2">
              {(["top", "middle", "bottom"] as const).map((align) => (
                <button
                  key={align}
                  type="button"
                  onClick={() => onVerticalAlignChange(align)}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all capitalize ${
                    verticalAlign === align
                      ? "bg-primary-main text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {align}
                </button>
              ))}
            </div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Background</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={backgroundColor || "#ffffff"}
                  onChange={(e) => onBackgroundColorChange(e.target.value)}
                  className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={backgroundColor}
                  onChange={(e) => onBackgroundColorChange(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm"
                  placeholder="#ffffff"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={textColor || "#000000"}
                  onChange={(e) => onTextColorChange(e.target.value)}
                  className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={textColor}
                  onChange={(e) => onTextColorChange(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm"
                  placeholder="#000000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Border Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={borderColor || "#e5e7eb"}
                  onChange={(e) => onBorderColorChange(e.target.value)}
                  className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={borderColor}
                  onChange={(e) => onBorderColorChange(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm"
                  placeholder="#e5e7eb"
                />
              </div>
            </div>
          </div>

          {/* Responsive Width & Padding */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Responsive Settings</h4>
            <div className="space-y-2">
              {(Object.keys(BREAKPOINTS) as BreakpointKey[]).map((key) => {
                const Icon = breakpointIcons[key];
                const isExpanded = expandedBreakpoint === key;
                const bp = BREAKPOINTS[key];

                return (
                  <div key={key} className="border rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedBreakpoint(isExpanded ? null : key)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        <span className="font-medium text-sm">{bp.label}</span>
                        <span className="text-xs text-gray-500">({bp.description})</span>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="p-4 space-y-4 bg-white">
                        {/* Width */}
                        {widthType !== "auto" && (
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">
                              Width {widthType === "percent" ? "(%)" : "(px)"}
                            </label>
                            {widthType === "percent" ? (
                              <select
                                value={columnWidths[key]}
                                onChange={(e) =>
                                  onColumnWidthsChange({ ...columnWidths, [key]: e.target.value })
                                }
                                className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm bg-white"
                              >
                                {percentOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="number"
                                min="20"
                                max="1000"
                                placeholder="e.g., 120"
                                value={columnWidths[key]}
                                onChange={(e) =>
                                  onColumnWidthsChange({ ...columnWidths, [key]: e.target.value })
                                }
                                className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm"
                              />
                            )}
                          </div>
                        )}

                        {/* Padding */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-600">Padding (px)</label>
                            <button
                              type="button"
                              onClick={() => copyPaddingToAll(key)}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              Copy to all breakpoints
                            </button>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            {(["top", "right", "bottom", "left"] as const).map((side) => (
                              <div key={side}>
                                <label className="block text-xs text-gray-500 mb-1 capitalize">
                                  {side}
                                </label>
                                <select
                                  value={padding[key]?.[side] || defaultPadding[side]}
                                  onChange={(e) => updatePaddingForBreakpoint(key, side, e.target.value)}
                                  className="w-full border border-gray-300 px-2 py-1 rounded text-sm bg-white"
                                >
                                  {paddingOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Preview</h4>
            <div className="bg-gray-100 p-4 rounded">
              <table className="border-collapse">
                <tbody>
                  <tr>
                    <td
                      style={{
                        backgroundColor: backgroundColor || "#ffffff",
                        color: textColor || "#000000",
                        borderColor: borderColor || "#e5e7eb",
                        borderWidth: "1px",
                        borderStyle: "solid",
                        textAlign: textAlign,
                        verticalAlign: verticalAlign,
                        padding: padding.xs
                          ? `${padding.xs.top}px ${padding.xs.right}px ${padding.xs.bottom}px ${padding.xs.left}px`
                          : "8px 12px",
                        width: widthType !== "auto" && columnWidths.xs
                          ? `${columnWidths.xs}${widthType === "percent" ? "%" : "px"}`
                          : "auto",
                        minWidth: "100px",
                      }}
                      className="text-sm"
                    >
                      Column {columnIndex + 1} Content
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-2">
                Showing mobile (xs) preview
              </p>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2 border-t mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90 transition-all"
            >
              Apply Column Styles
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

